import { Download, Eye, FileText, Plus, Pencil, Trash2, Search } from "lucide-react";
import { FC, useState } from "react";
import { useDetailBookingStore } from "../detail-booking.store";
import { BookingDocumentUploadModal } from "./document-upload-modal";
import { DocumentEditModal } from "./document-edit-modal";
import { useGetFiles, useDeleteFile } from "~/services/file";
import { SortOrder } from "~/utils/constants";
import { Spin, Badge, Popconfirm, Select } from "antd";
import { capitalize } from "~/utils";
import { TFile } from "~/types";
import { useNotification } from "~/hooks";

type Props = {};

export const Documents: FC<Props> = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingDocument, setEditingDocument] = useState<TFile | null>(null);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const { booking } = useDetailBookingStore();
  const { success, error } = useNotification();
  const deleteMutation = useDeleteFile();

  // Get documents associated with this booking
  const { data: documentsData, isLoading, refetch } = useGetFiles({
    jobTicketId: booking?.id,
    sortByCreatedAt: SortOrder.DESC,
    page: currentPage,
    pageSize,
    ...(searchQuery ? { search: searchQuery } : {}),
  });

  const documents = documentsData?.items || [];
  const meta = documentsData?.meta;
  const totalPages = meta?.totalPages || 1;
  const totalItems = meta?.totalItems || 0;

  const handleUploadClick = () => {
    setModalVisible(true);
  };

  const handleUploadSuccess = () => {
    refetch();
  };

  const handleDocumentSelect = (docId: string) => {
    setSelectedDocId(docId === selectedDocId ? null : docId);
  };

  const handleEditClick = (doc: TFile) => {
    setEditingDocument(doc);
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    refetch();
    setEditModalVisible(false);
    setEditingDocument(null);
  };

  const handleDeleteClick = async (docId: string) => {
    try {
      await deleteMutation.mutateAsync(docId);
      success({ message: "Document deleted successfully" });
      refetch();
      if (selectedDocId === docId) {
        setSelectedDocId(null);
      }
    } catch (err) {
      error({ message: "Failed to delete document" });
    }
  };

  // Find the selected document for preview
  const selectedDocument = documents.find((doc) => doc.id === selectedDocId);

  if (!booking) {
    return null;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium">Documents</h3>
        <button
          onClick={handleUploadClick}
          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-1"
        >
          <Plus size={16} />
          <span>Upload Document</span>
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Show</span>
          <Select
            value={pageSize}
            onChange={(value) => {
              setPageSize(value);
              setCurrentPage(1);
            }}
            options={[
              { value: 10, label: "10" },
              { value: 20, label: "20" },
            ]}
            size="small"
            className="w-20"
          />
          <span>per page</span>
        </div>
      </div>

      <Spin spinning={isLoading}>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Related Service</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Staff Access</th>
                <th className="px-4 py-3">Client Access</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {documents.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-gray-500">
                    No documents found for this booking
                  </td>
                </tr>
              ) : (
                documents.map((doc) => (
                  <tr
                    key={doc.id}
                    className={`hover:bg-gray-50 ${
                      doc.id === selectedDocId ? "bg-blue-50" : ""
                    }`}
                  >
                    <td className="px-4 py-3 font-medium">{doc.title}</td>
                    <td className="px-4 py-3 text-sm capitalize">
                      {doc.type?.toLowerCase()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {doc.serviceTicket?.serial || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {new Date(doc.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm capitalize">
                      <Badge
                        status="success"
                        text={doc.status
                          ? capitalize(doc.status.toLowerCase())
                          : "Sent"}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Badge
                        status={doc.visibleToStaff ? "success" : "default"}
                        text={doc.visibleToStaff ? "Visible" : "Hidden"}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Badge
                        status={doc.visibleToClient ? "success" : "default"}
                        text={doc.visibleToClient ? "Visible" : "Hidden"}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex space-x-1">
                        <button
                          className="p-1 text-gray-400 hover:text-blue-600"
                          onClick={() => handleDocumentSelect(doc.id)}
                          title="Preview"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="p-1 text-gray-400 hover:text-blue-600"
                          onClick={() => window.open(doc.filePath, "_blank")}
                          title="Download"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          className="p-1 text-gray-400 hover:text-amber-600"
                          onClick={() => handleEditClick(doc)}
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <Popconfirm
                          title="Delete Document"
                          description="Are you sure you want to delete this document?"
                          onConfirm={() => handleDeleteClick(doc.id)}
                          okText="Delete"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                        >
                          <button
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </Popconfirm>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span>
              Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalItems)} of {totalItems} documents
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <span className="px-3">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-2 py-1 border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium mb-4">Document Preview</h3>
            <div className="bg-gray-50 border rounded-lg p-4 h-96 flex items-center justify-center">
              {selectedDocument ? (
                selectedDocument.filePath ? (
                  selectedDocument.filePath.toLowerCase().endsWith(".pdf") ? (
                    <iframe
                      src={`${selectedDocument.filePath}#toolbar=0`}
                      className="w-full h-full"
                      title={selectedDocument.title}
                    />
                  ) : (
                    <img
                      src={selectedDocument.filePath}
                      alt={selectedDocument.title}
                      className="max-w-full max-h-full object-contain"
                    />
                  )
                ) : (
                  <div className="text-center text-gray-400">
                    <FileText size={48} className="mx-auto mb-2" />
                    <p>Unable to preview this document</p>
                  </div>
                )
              ) : (
                <div className="text-center text-gray-400">
                  <FileText size={48} className="mx-auto mb-2" />
                  <p>Select a document to preview</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-4">Document Details</h3>
            <div className="bg-white border rounded-lg p-4 h-96">
              {selectedDocument ? (
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-gray-500">Title</div>
                    <div className="font-medium">{selectedDocument.title}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Type</div>
                    <div>
                      {capitalize(
                        selectedDocument.type?.toLowerCase() || "Document"
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Uploaded on</div>
                    <div>{new Date(selectedDocument.createdAt).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Status</div>
                    <div>
                      {capitalize(selectedDocument.status?.toLowerCase() || "Sent")}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Staff Visibility</div>
                    <div>
                      <Badge
                        status={selectedDocument.visibleToStaff ? "success" : "default"}
                        text={selectedDocument.visibleToStaff ? "Visible to Staff" : "Hidden from Staff"}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Client Visibility</div>
                    <div>
                      <Badge
                        status={selectedDocument.visibleToClient ? "success" : "default"}
                        text={selectedDocument.visibleToClient ? "Visible to Client" : "Hidden from Client"}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Actions</div>
                    <div className="flex space-x-2 mt-2">
                      <button
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center space-x-1"
                        onClick={() => window.open(selectedDocument.filePath, "_blank")}
                      >
                        <Eye size={14} />
                        <span>Open</span>
                      </button>
                      <button className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center space-x-1">
                        <Download size={14} />
                        <span>Download</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Select a document to view details</p>
              )}
            </div>
          </div>
        </div>
      </Spin>

      {/* Document Upload Modal */}
      <BookingDocumentUploadModal
        visible={modalVisible}
        booking={booking}
        onClose={() => setModalVisible(false)}
        onSuccess={handleUploadSuccess}
      />

      {/* Document Edit Modal */}
      <DocumentEditModal
        visible={editModalVisible}
        document={editingDocument}
        onClose={() => {
          setEditModalVisible(false);
          setEditingDocument(null);
        }}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};
